import * as React from "react";
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";

import { cn } from "../../lib/utils";
import { resolveRender } from "../../lib/resolve-render";

/**
 * Migrated from `@radix-ui/react-tooltip` to Base UI.
 *
 * This is the widest surface in the burndown — **414 triggers across 175
 * files**, 406 of them using `asChild` — so no call site changes and every
 * difference is absorbed here. Six of them, in rough order of how quietly they
 * would have failed:
 *
 *  1. **The open delay changes owner *and* default.** Radix resolves it from
 *     the nearest `TooltipProvider`; Base UI resolves it from the *trigger*,
 *     falling back to **600ms** where our provider falls back to **0**.
 *     Adopting that default would put a visible lag on every tooltip in the
 *     app, and no screenshot could see it — a screenshot waits for the tooltip
 *     to appear. `TooltipProvider` below keeps supplying 0, which is what the
 *     trigger resolves against.
 *  2. **The arrow changes parent.** Radix nests `Arrow` inside `Content`,
 *     which is why the old classes had to pull it back up over the content's
 *     edge by hand (`translate-y-[calc(-50%_-_2px)]`). Base UI makes it a
 *     sibling of `Popup` under `Positioner` and positions it itself, so that
 *     manual correction becomes a per-side nudge instead.
 *  3. **z-index stops propagating.** `z-[1500]` reaches the screen today only
 *     because Radix copies the content's computed z-index onto the positioned
 *     wrapper it creates — the content itself is `position: static`, where
 *     z-index does nothing at all. Base UI does no such copying, so the value
 *     moves to the positioner, which is the element it can actually apply to.
 *  4. **`Content` is two elements now.** `Positioner` owns placement, `Popup`
 *     owns the box, so `side` / `align` / `sideOffset` stop being siblings of
 *     `className`. `TooltipContent` keeps the Radix signature and splits it.
 *  5. **The transform-origin variable is renamed and re-homed.**
 *     `--radix-tooltip-content-transform-origin` becomes `--transform-origin`,
 *     set on the positioner — custom properties inherit, so the popup still
 *     reads it. This is the one with no symptom: an unresolvable
 *     `transform-origin` is not an error, it falls back to `50% 50%`.
 *  6. **The state attributes.** `data-[state=closed]` becomes
 *     `data-[ending-style]`, and the entry animation becomes `data-[open]`.
 *  7. **The accessible relationship disappears entirely.** Radix links trigger
 *     to tooltip with `aria-describedby`, pointing at a visually hidden
 *     `role="tooltip"` copy of the text. Base UI 1.7 emits neither — no role,
 *     no `aria-describedby` — so a screen-reader user gets a button with no
 *     description at all. `TooltipA11yContext` below puts the relationship
 *     back. This one had no visual symptom and no type error; it surfaced only
 *     because a single consumer's unit test happened to assert
 *     `getByRole('tooltip')`.
 *
 * One difference is deliberately preserved rather than repaired — see
 * `TooltipProvider`.
 */

/**
 * Radix defaults `collisionPadding` to `0`; Base UI defaults it to `5`. Left
 * alone, every tooltip that reaches a viewport edge shifts inward by five
 * pixels. Pinned to Radix's value so all 175 consumers keep their placement.
 */
const RADIX_COLLISION_PADDING = 0;

/**
 * The arrow's own size, in pixels, matching the `size-2.5` below.
 *
 * It has to be added to `sideOffset` because **Radix folded it in silently**:
 * its popper offsets by `sideOffset + arrowHeight`, measuring the arrow element
 * and pushing the content that much further from the trigger. Base UI does no
 * such thing, so a faithful `sideOffset={0}` puts every tooltip in the app ten
 * pixels closer to its trigger than it is today — flush against it, with the
 * arrow overlapping the trigger rather than pointing at it.
 *
 * This is the difference the screenshots caught, and it was worth catching:
 * ten pixels on 414 tooltips is not something a reviewer would spot in a diff,
 * and every behavioural assertion in the baseline passed without it.
 */
const ARROW_SIZE_PX = 10;

/**
 * Module scope so it is not a fresh object on every render.
 *
 * The value is the one the Radix wrapper carried, with its original note:
 * "Higher than dialog z-50 and MUI components to ensure proper stacking".
 */
const TOOLTIP_Z_INDEX: React.CSSProperties = { zIndex: 1500 };

/**
 * Carries the tooltip's id from the root down to both halves, so the trigger
 * can point `aria-describedby` at the popup.
 *
 * Base UI supplies no accessible relationship of its own — see point 7 above —
 * and the two halves are separate components with no other shared state, so
 * the id has to be minted once at the root where both can see it.
 */
const TooltipA11yContext = React.createContext<string | undefined>(undefined);

type TooltipProviderProps = Omit<
  TooltipPrimitive.Provider.Props,
  "delay" | "timeout"
> & {
  /** Radix's spelling of Base UI's `delay`. */
  delayDuration?: number;
  /** Radix's spelling of Base UI's `timeout`. */
  skipDelayDuration?: number;
};

/**
 * **The nesting here is load-bearing, and it preserves a bug on purpose.**
 *
 * `Tooltip` below renders its own `TooltipProvider`, so a consumer's outer
 * `<TooltipProvider delayDuration={200}>` is shadowed by the inner default of
 * 0 and has never had any effect. Seven call sites do exactly that today and
 * get instant tooltips.
 *
 * Reproducing that is deliberate. Repairing it here would change the timing of
 * seven call sites inside a refactor PR, where no reviewer is looking for a UX
 * change; it belongs in its own change where it can be reviewed as one. The
 * baseline pins the current behaviour so that later PR has to state its intent.
 */
function TooltipProvider({
  delayDuration = 0,
  skipDelayDuration,
  children,
  ...props
}: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider
      delay={delayDuration}
      timeout={skipDelayDuration}
      {...props}
    >
      {children}
    </TooltipPrimitive.Provider>
  );
}

type TooltipProps = Omit<TooltipPrimitive.Root.Props, "children"> & {
  /**
   * Narrowed from Base UI's, which also accepts a render function receiving the
   * active trigger's `payload`. That form belongs to Base UI's `handle` API,
   * which has no Radix equivalent and therefore no caller here.
   */
  children?: React.ReactNode;
  /** Radix's spelling; Base UI's equivalent lives on the trigger. */
  delayDuration?: number;
  /** Radix's spelling of Base UI's `disableHoverablePopup`. */
  disableHoverableContent?: boolean;
};

function Tooltip({
  children,
  delayDuration,
  disableHoverableContent,
  ...props
}: TooltipProps) {
  const contentId = React.useId();

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipPrimitive.Root
        disableHoverablePopup={disableHoverableContent}
        {...props}
      >
        <TooltipA11yContext.Provider value={contentId}>
          {children}
        </TooltipA11yContext.Provider>
      </TooltipPrimitive.Root>
    </TooltipProvider>
  );
}

type TooltipTriggerProps = Omit<TooltipPrimitive.Trigger.Props, "className"> & {
  className?: string;
  asChild?: boolean;
};

/**
 * The trigger passes no `delay` of its own, and that is deliberate rather than
 * an omission. Base UI resolves the trigger's delay as
 * `delay ?? providerDelay ?? 600`, and `Tooltip` always renders a provider —
 * so the wrapper's `delayDuration` reaches every trigger through it and the
 * 600ms default is never the value that wins.
 *
 * Threading it explicitly instead would work too, and be immune to a change in
 * how Base UI resolves the provider. It is left out because the baseline's
 * `opens within a frame of the hover` assertion already fails loudly if that
 * resolution ever stops working, which is the guard that actually matters —
 * and an unused context is a worse way to state the same thing.
 */
function TooltipTrigger({
  asChild = false,
  render,
  children,
  ...props
}: TooltipTriggerProps) {
  const contentId = React.useContext(TooltipA11yContext);
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      // Set unconditionally rather than only while open, because the trigger
      // cannot see the open state without giving up `render` to a state
      // callback — and `render` is how `asChild` works at 406 call sites. An
      // `aria-describedby` pointing at an element that is not in the document
      // is ignored, which is exactly the "closed" behaviour wanted here.
      aria-describedby={contentId}
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </TooltipPrimitive.Trigger>
  );
}

/**
 * Radix's placement props live on the same element as the styling; Base UI's
 * live one level up. This keeps the Radix signature and splits it.
 */
type TooltipContentProps = Omit<
  TooltipPrimitive.Popup.Props,
  | "className"
  | "side"
  | "align"
  | "sideOffset"
  | "alignOffset"
  | "collisionBoundary"
  | "collisionPadding"
> &
  Pick<
    TooltipPrimitive.Positioner.Props,
    | "side"
    | "align"
    | "sideOffset"
    | "alignOffset"
    | "collisionBoundary"
    | "collisionPadding"
  > & { className?: string };

function TooltipContent({
  className,
  side,
  align,
  sideOffset = 0,
  alignOffset,
  collisionBoundary,
  collisionPadding = RADIX_COLLISION_PADDING,
  hidden,
  children,
  ...props
}: TooltipContentProps) {
  const contentId = React.useContext(TooltipA11yContext);
  // `sideOffset` keeps its Radix meaning for callers — the gap they asked for —
  // and the arrow's height is folded in here the way Radix folded it in there.
  const offsetWithArrow =
    typeof sideOffset === "number" ? sideOffset + ARROW_SIZE_PX : sideOffset;

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        data-slot="tooltip-positioner"
        side={side}
        align={align}
        sideOffset={offsetWithArrow}
        alignOffset={alignOffset}
        collisionBoundary={collisionBoundary}
        collisionPadding={collisionPadding}
        hidden={hidden}
        // The local stacking override, unchanged in value and moved in place.
        // z-index only applies to a positioned element, and under Base UI this
        // is the one that is positioned — the popup is not.
        //
        // Written as an inline style rather than `className="z-[1500]"`, which
        // is also how Radix applied it to its own wrapper. The class form put
        // a bracketed name (`z-\[1500\]`) on a portalled ancestor, and
        // Handsontable builds CSS selectors by interpolating ancestor class
        // names — it turned that into `div.z-,,,B1500,,,D ...` and threw
        // `SyntaxError: not a valid selector`, taking the AS/400 grid with it.
        style={TOOLTIP_Z_INDEX} // TODO: remove this when we remove mui
      >
        <TooltipPrimitive.Popup
          // Radix put the role on a visually hidden copy of the text and left
          // the visible box unlabelled; putting it on the box itself is the
          // same relationship with one element instead of two.
          role="tooltip"
          id={contentId}
          data-slot="tooltip-content"
          className={cn(
            "bg-primary text-primary-foreground data-[open]:animate-in data-[open]:fade-in-0 data-[open]:zoom-in-95 data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[ending-style]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 w-fit origin-(--transform-origin) rounded-md px-3 py-1.5 text-xs text-balance",
            className,
          )}
          {...props}
        >
          {children}
          {/*
            **Inside the popup, not beside it.** Base UI's own examples put the
            arrow next to `Popup` under `Positioner`, and that is what this
            wrapper did first — at the cost of three separate bugs, each one a
            thing the arrow no longer inherited: it ignored a caller's `hidden`
            (#6231) and it stayed opaque while the popup faded (#6239), each
            needing its own patch.

            It belongs here because `Arrow` reads its position from React
            *context*, not from its DOM parent — so nesting it changes nothing
            about where it lands, while restoring everything it inherited under
            Radix, where the arrow was a child of `Content`.

            This depends on the popup staying **unpositioned**. The arrow's
            `position: absolute` resolves against the nearest positioned
            ancestor — the positioner — so adding `relative` to the popup would
            silently reparent that containing block and move the arrow. The
            baseline asserts it.
          */}
          <TooltipPrimitive.Arrow
            data-slot="tooltip-arrow"
            className="bg-primary size-2.5 rotate-45 rounded-[2px] data-[side=bottom]:bottom-full data-[side=bottom]:translate-y-[calc(50%_+_2px)] data-[side=left]:left-full data-[side=left]:translate-x-[calc(-50%_-_2px)] data-[side=right]:right-full data-[side=right]:translate-x-[calc(50%_+_2px)] data-[side=top]:top-full data-[side=top]:translate-y-[calc(-50%_-_2px)]"
          />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
